/**
 * TopBar — the portal's main navigation (Design, Home handoff 1 Oct).
 *
 * Six destinations on a cream bar, replacing the 10-item sidebar:
 * Home, Sessions, Drills, Availability, Earnings, Profile. Pages that
 * aren't redesigned yet (Session prep, Week view, Upload, Proposals) stay
 * reachable as sub-tabs under Drills until its screen is sketched. Sessions
 * became one screen (redesign screen 4); Upload is a link on it.
 *
 * Narrow screens (Design, 3 Oct, studio-menu-overlap: the labels overlapped
 * and a tap on Drills opened Availability). Measured with LIVE and a
 * proposals count showing, the full bar needs ~1150px in Indonesian and the
 * compact row ~795px, so (breakpoints moved up, never smaller text):
 *   - 1180px and up: the full bar, equal-width capsules;
 *   - 840 to 1179px: a compact row, labels sized to their text, "Studio"
 *     dropped from the logo;
 *   - under 840px (phones, upright iPads): a menu button carrying the
 *     proposals count, the section's name, LIVE and the avatar; the button
 *     opens a full-screen list.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useI18n } from '@/lib/i18n';
import type { FacilitatorPage } from '@/types/facilitator-portal';

export type TopSection = 'home' | 'sessions' | 'drills' | 'availability' | 'earnings' | 'profile';

/** Which top-bar section a page belongs to. */
function sectionOf(page: FacilitatorPage): TopSection {
  switch (page) {
    case 'dashboard': return 'home';
    case 'sessions': case 'prep': case 'schedule': case 'wrapup': return 'sessions';
    case 'active-drills': case 'proposals': return 'drills';
    case 'availability': return 'availability';
    case 'earnings': return 'earnings';
    default: return 'profile';
  }
}

const SECTIONS: { id: TopSection; label: string; page: FacilitatorPage }[] = [
  { id: 'home', label: 'nav.home', page: 'dashboard' },
  { id: 'sessions', label: 'nav.sessions', page: 'sessions' },
  { id: 'drills', label: 'nav.drills', page: 'active-drills' },
  { id: 'availability', label: 'nav.availability', page: 'availability' },
  { id: 'earnings', label: 'nav.earnings', page: 'earnings' },
  { id: 'profile', label: 'nav.profile', page: 'profile' },
];

/** Sub-tabs for the sections that still group several pages. */
const SUB: Partial<Record<TopSection, { page: FacilitatorPage; label: string }[]>> = {
};

interface Props {
  currentPage: FacilitatorPage;
  onNavigate: (page: FacilitatorPage) => void;
  displayName: string;
  hasLiveSession: boolean;
  proposalCount: number;
  /** The notification bell (StudioNotices), left of EN · ID. */
  bell?: ReactNode;
}

export default function TopBar({ currentPage, onNavigate, displayName, hasLiveSession, proposalCount, bell }: Props) {
  const { t, locale, setLocale } = useI18n();
  const section = sectionOf(currentPage);
  const sub = SUB[section];
  const initial = (displayName.trim()[0] ?? 'P').toUpperCase();
  const [menuOpen, setMenuOpen] = useState(false);
  const current = SECTIONS.find((s) => s.id === section) ?? SECTIONS[0];

  // The phone menu closes on Escape, and whenever the page changes.
  useEffect(() => { setMenuOpen(false); }, [currentPage]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const count = (n: number, extra = '') => (
    <span className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--fp-status-warn)] px-1.5 text-xs font-extrabold text-[var(--fp-ink)] ${extra}`}>{n}</span>
  );
  const liveChip = hasLiveSession && (
    // Ink chip with a lime dot: lime on cream is too faint (Design, 1 Oct).
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--fp-ink)] px-2.5 py-1 text-xs font-extrabold tracking-[0.14em] text-[var(--fp-cream)]">
      <span className="h-2 w-2 rounded-full bg-[var(--fp-status-live)]" aria-hidden />
      LIVE
    </span>
  );
  const langButton = (tone: 'ink' | 'cream') => (
    <button
      type="button"
      onClick={() => setLocale(locale === 'en' ? 'id' : 'en')}
      aria-label={t('lang.label')}
      className={`min-h-[var(--fp-tap)] shrink-0 rounded-full border-[1.5px] px-3.5 text-[13px] font-extrabold ${tone === 'ink' ? 'border-[var(--fp-ink)]' : 'border-[var(--fp-cream)] text-[var(--fp-cream)]'}`}
    >
      {locale === 'en' ? 'EN · ID' : 'ID · EN'}
    </button>
  );
  const avatar = (
    <button
      type="button"
      onClick={() => onNavigate('profile')}
      aria-label={t('nav.open_profile')}
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--fp-ink)] text-sm font-extrabold text-[var(--fp-cream)]"
    >
      {initial}
    </button>
  );
  const capsule = (s: (typeof SECTIONS)[number], compact: boolean) => {
    const active = s.id === section;
    return (
      <button
        key={s.id}
        type="button"
        onClick={() => onNavigate(s.page)}
        aria-current={active ? 'page' : undefined}
        className={`inline-flex min-h-[var(--fp-tap)] items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-sm font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--fp-ink)] ${compact ? 'px-2' : 'px-3'} ${
          active ? 'bg-[var(--fp-ink)] text-[var(--fp-cream)]' : 'hover:bg-[rgba(10,10,12,0.1)]'
        }`}
      >
        {t(s.label)}
        {s.id === 'drills' && proposalCount > 0 && count(proposalCount)}
      </button>
    );
  };

  return (
    <div className="relative shrink-0">
      <header className="flex min-h-16 items-center gap-2 bg-[var(--fp-cream)] px-3 text-[var(--fp-ink)] min-[840px]:gap-3 min-[840px]:px-4 min-[1180px]:gap-6 min-[1180px]:px-6">
        {/* Phone: menu button (with the proposals count) and the section's name. */}
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-controls="fp-phone-menu"
          aria-label={menuOpen ? t('nav.close_menu') : t('nav.menu')}
          className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-[1.5px] border-[var(--fp-ink)] min-[840px]:hidden"
        >
          {menuOpen ? (
            <span className="text-[22px] font-bold leading-none" aria-hidden>×</span>
          ) : (
            <span className="flex flex-col gap-[5px]" aria-hidden>
              <i className="block h-0.5 w-[18px] rounded bg-[var(--fp-ink)]" />
              <i className="block h-0.5 w-[18px] rounded bg-[var(--fp-ink)]" />
              <i className="block h-0.5 w-[18px] rounded bg-[var(--fp-ink)]" />
            </span>
          )}
          {!menuOpen && proposalCount > 0 && count(proposalCount, 'absolute -right-1 -top-1')}
        </button>
        <span className="flex-1 truncate text-base font-extrabold min-[840px]:hidden">{t(current.label)}</span>

        {/* Tablet and up: the logo ("Studio" only on wide screens). */}
        <div className="hidden whitespace-nowrap font-display text-lg font-black tracking-tight min-[840px]:block">
          Playbook <span className="hidden font-medium opacity-70 min-[1180px]:inline">{t('nav.facilitator')}</span>
        </div>
        {/* Tablet: a compact row sized to the labels. */}
        <nav aria-label={t('nav.menu')} className="hidden flex-1 justify-center gap-0.5 min-[840px]:flex min-[1180px]:hidden">
          {SECTIONS.map((s) => capsule(s, true))}
        </nav>
        {/* Wide: equal-width capsules (Nino, 2 Oct): an inline grid with 1fr
            columns sizes every column to the widest label. */}
        <nav aria-label={t('nav.menu')} className="hidden flex-1 min-[1180px]:block">
          <div className="inline-grid grid-flow-col auto-cols-fr gap-1">
            {SECTIONS.map((s) => capsule(s, false))}
          </div>
        </nav>

        <div className="flex shrink-0 items-center gap-2 min-[840px]:gap-3">
          {liveChip}
          {bell}
          <span className="hidden min-[840px]:inline-flex">{langButton('ink')}</span>
          {avatar}
        </div>
      </header>

      {/* Phone: the full-screen list. */}
      {menuOpen && (
        <nav
          id="fp-phone-menu"
          aria-label={t('nav.menu')}
          className="fixed inset-x-0 bottom-0 top-16 z-40 flex flex-col gap-1.5 overflow-y-auto bg-[var(--fp-ink)] px-4 py-3 min-[840px]:hidden"
        >
          {SECTIONS.map((s) => {
            const active = s.id === section;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => { setMenuOpen(false); onNavigate(s.page); }}
                aria-current={active ? 'page' : undefined}
                className={`flex min-h-14 items-center justify-between rounded-[14px] border px-4 text-left text-lg font-bold ${
                  active ? 'border-[var(--fp-cream)] bg-[var(--fp-cream)] text-[var(--fp-ink)]' : 'border-[var(--fp-line)] text-[var(--fp-cream)]'
                }`}
              >
                <span>{t(s.label)}</span>
                {s.id === 'drills' && proposalCount > 0 && count(proposalCount)}
              </button>
            );
          })}
          <div className="mt-auto flex items-center gap-3 pb-4 pt-2">
            {langButton('cream')}
            <span className="flex-1 truncate text-sm text-[var(--fp-cream-62)]">{displayName}</span>
          </div>
        </nav>
      )}

      {sub && (
        <nav aria-label={t(SECTIONS.find((s) => s.id === section)!.label)} className="flex gap-1 overflow-x-auto border-b border-[var(--fp-line)] bg-background px-4 sm:px-6">
          {sub.map((s) => {
            const active = s.page === currentPage;
            return (
              <button
                key={s.page}
                type="button"
                onClick={() => onNavigate(s.page)}
                aria-current={active ? 'page' : undefined}
                className={`-mb-px min-h-[var(--fp-tap)] shrink-0 border-b-2 px-3 text-sm font-semibold transition-colors ${
                  active ? 'border-[var(--fp-cream)] text-foreground' : 'border-transparent text-[var(--fp-cream-62)] hover:text-foreground'
                }`}
              >
                {t(s.label)}
                {s.page === 'proposals' && proposalCount > 0 && (
                  <span className="ml-1.5 rounded-full bg-[var(--fp-status-warn)] px-1.5 text-xs font-bold text-[var(--fp-ink)]">{proposalCount}</span>
                )}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
